import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  Boxes, Factory, Truck, Calculator, Wallet, LineChart, Users,
  Workflow, ShieldCheck, Bell, BarChart3, FileText, Package, Receipt,
} from "lucide-react";
import { ServicePage, type ServicePageData } from "@/components/sections/service-page";

const CHILDREN: Record<string, ServicePageData & { metaDesc: string }> = {
  inventory: {
    eyebrow: "ERP · Modules",
    title: "Inventory Module",
    sub: "Real-time stock control across warehouses, channels and locations.",
    metaDesc: "ERP Inventory module: multi-warehouse stock, reorder points, barcode, lot/serial tracking and cycle counts.",
    intro:
      "Know exactly what you have, where it is and what to reorder. Multi-warehouse, lot and serial tracking, barcode workflows and automated reordering keep stock accurate and capital efficient.",
    features: [
      { icon: Boxes, title: "Multi-warehouse", description: "Unlimited locations with per-warehouse stock." },
      { icon: Package, title: "Lot & serial tracking", description: "Full traceability from receipt to delivery." },
      { icon: Bell, title: "Reorder points", description: "Automatic POs when stock hits min levels." },
      { icon: ShieldCheck, title: "Cycle counts", description: "Scheduled counts with variance reporting." },
      { icon: Workflow, title: "Barcode workflows", description: "Pick, pack, ship via handheld scanners." },
      { icon: BarChart3, title: "Stock valuation", description: "FIFO, weighted average and standard costing." },
    ],
    process: [
      { step: "01", title: "Map locations", description: "Warehouses, bins and storage zones." },
      { step: "02", title: "Import items", description: "SKUs, units, costs and barcodes." },
      { step: "03", title: "Configure rules", description: "Reorder points, ABC categories and counts." },
      { step: "04", title: "Train & go live", description: "Warehouse team training and dashboards." },
    ],
    deliverables: ["Item master", "Warehouse setup", "Barcode labels", "Reorder rules", "Stock reports", "Mobile picking app"],
    techStack: ["ERPNext", "Odoo Inventory", "Zebra scanners", "PostgreSQL", "Shopify API"],
    pricing: [
      { name: "Starter", price: "$3k", features: ["1 warehouse", "Up to 1k SKUs", "Reorder points"] },
      { name: "Business", price: "$9k", period: "project", highlighted: true, features: ["Multi-warehouse", "Lot/serial", "Barcode workflows"] },
      { name: "Enterprise", price: "Custom", features: ["WMS integration", "EDI", "Advanced costing"] },
    ],
    faqs: [
      { q: "Does it integrate with Shopify?", a: "Yes — real-time stock sync across all sales channels." },
      { q: "Can we use barcodes?", a: "Yes — print, scan and pick with handheld devices or phones." },
    ],
  },
  "sales-crm": {
    eyebrow: "ERP · Modules",
    title: "Sales & CRM Module",
    sub: "Pipeline, quotes, orders and customer history in one view.",
    metaDesc: "ERP Sales & CRM module: lead management, pipeline, quotes, sales orders and customer 360.",
    intro:
      "From first touch to repeat purchase, manage the whole customer journey inside your ERP — no separate CRM, no broken handoffs.",
    features: [
      { icon: Users, title: "Pipeline", description: "Drag-and-drop deal stages with probability." },
      { icon: FileText, title: "Quotes & orders", description: "Generate, send and convert in one click." },
      { icon: Bell, title: "Activity tracking", description: "Calls, emails and meetings logged to records." },
      { icon: BarChart3, title: "Forecasting", description: "Weighted pipeline and revenue forecasts." },
      { icon: Workflow, title: "Automation", description: "Triggers for follow-ups, alerts and assignments." },
      { icon: ShieldCheck, title: "Customer 360", description: "Orders, invoices, tickets in one timeline." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Sales process and stage definitions." },
      { step: "02", title: "Configure", description: "Pipelines, fields, roles and permissions." },
      { step: "03", title: "Migrate", description: "Import contacts, deals and history." },
      { step: "04", title: "Enable", description: "Train reps and connect email/calendar." },
    ],
    deliverables: ["Custom pipelines", "Quote templates", "Sales dashboards", "Email integration", "Mobile CRM"],
    techStack: ["ERPNext", "Odoo CRM", "HubSpot Sync", "Gmail/Outlook", "Twilio"],
    pricing: [
      { name: "Starter", price: "$2.5k", features: ["1 pipeline", "Up to 5 users", "Email sync"] },
      { name: "Business", price: "$7k", period: "project", highlighted: true, features: ["Multi-pipeline", "Automations", "Forecasting"] },
      { name: "Enterprise", price: "Custom", features: ["Custom workflows", "Territory mgmt", "Advanced reporting"] },
    ],
    faqs: [
      { q: "Can we migrate from HubSpot?", a: "Yes — contacts, deals, activities and notes." },
      { q: "Is there a mobile app?", a: "Yes — pipeline, calls and check-ins on iOS and Android." },
    ],
  },
  procurement: {
    eyebrow: "ERP · Modules",
    title: "Procurement Module",
    sub: "Source, approve and pay suppliers without spreadsheets.",
    metaDesc: "ERP Procurement module: supplier management, RFQs, purchase orders, 3-way matching and approvals.",
    intro:
      "Streamline buying from request to payment with structured approvals, supplier scorecards and full 3-way matching against POs and receipts.",
    features: [
      { icon: Truck, title: "Supplier management", description: "Vendors, contacts, terms and tax info." },
      { icon: FileText, title: "RFQs & quotes", description: "Compare quotes side-by-side and award." },
      { icon: Workflow, title: "PO approvals", description: "Multi-level approvals by value and category." },
      { icon: ShieldCheck, title: "3-way matching", description: "PO ↔ receipt ↔ invoice reconciliation." },
      { icon: BarChart3, title: "Spend analytics", description: "Spend by supplier, category and project." },
      { icon: Bell, title: "Contract reminders", description: "Renewal and price-break alerts." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Current procurement workflow and rules." },
      { step: "02", title: "Configure", description: "Approval matrix, categories and forms." },
      { step: "03", title: "Onboard suppliers", description: "Import vendors, terms and catalogs." },
      { step: "04", title: "Go live", description: "Train buyers and approvers." },
    ],
    deliverables: ["Vendor master", "PO templates", "Approval matrix", "RFQ workflow", "Spend dashboards"],
    techStack: ["ERPNext", "Odoo Purchase", "DocuSign", "PostgreSQL"],
    pricing: [
      { name: "Starter", price: "$3k", features: ["Basic POs", "Up to 50 vendors", "Single approver"] },
      { name: "Business", price: "$9k", period: "project", highlighted: true, features: ["Multi-level approvals", "3-way matching", "RFQs"] },
      { name: "Enterprise", price: "Custom", features: ["Punchout catalogs", "EDI", "Spend analytics"] },
    ],
    faqs: [
      { q: "Can we set spending limits?", a: "Yes — per role, department or category with auto-escalation." },
      { q: "Does it integrate with accounting?", a: "Yes — bills post automatically once matched." },
    ],
  },
  manufacturing: {
    eyebrow: "ERP · Modules",
    title: "Manufacturing Module",
    sub: "BOMs, work orders and shop-floor control on one platform.",
    metaDesc: "ERP Manufacturing module: BOM, MRP, work orders, routing, capacity planning and shop-floor tracking.",
    intro:
      "Plan, schedule and execute production with multi-level BOMs, MRP, routing and real-time shop-floor tracking — fully integrated with inventory and finance.",
    features: [
      { icon: Factory, title: "Multi-level BOMs", description: "Sub-assemblies, alternates and versioning." },
      { icon: Workflow, title: "MRP", description: "Net requirements with lead-time planning." },
      { icon: Boxes, title: "Work orders", description: "Routing, operations and time tracking." },
      { icon: BarChart3, title: "Capacity planning", description: "Work-center load and bottleneck analysis." },
      { icon: ShieldCheck, title: "Quality control", description: "Inspections at receipt, in-process and final." },
      { icon: Bell, title: "Shop-floor terminal", description: "Tablet-based job clock and material issue." },
    ],
    process: [
      { step: "01", title: "Model products", description: "BOMs, routings and work centers." },
      { step: "02", title: "Configure MRP", description: "Planning rules, lead times and safety stock." },
      { step: "03", title: "Pilot line", description: "Run one product line end-to-end." },
      { step: "04", title: "Scale", description: "Roll out to all lines and shifts." },
    ],
    deliverables: ["BOM library", "Routing setup", "MRP rules", "Shop-floor terminal", "Production reports"],
    techStack: ["ERPNext", "Odoo MRP", "OPC-UA", "PostgreSQL", "MES connectors"],
    pricing: [
      { name: "Starter", price: "$6k", features: ["Single line", "Basic BOM", "Work orders"] },
      { name: "Business", price: "$18k", period: "project", highlighted: true, features: ["MRP", "Routing", "Capacity planning"] },
      { name: "Enterprise", price: "Custom", features: ["MES integration", "Quality module", "OEE tracking"] },
    ],
    faqs: [
      { q: "Discrete or process manufacturing?", a: "Both — discrete, process, batch and repetitive are supported." },
      { q: "Can it talk to machines?", a: "Yes — via OPC-UA, MQTT or REST for live machine data." },
    ],
  },
  accounting: {
    eyebrow: "ERP · Finance",
    title: "Accounting Module",
    sub: "Double-entry, multi-currency, tax-ready financials.",
    metaDesc: "ERP Accounting module: GL, AP, AR, bank reconciliation, multi-currency and tax reporting.",
    intro:
      "A full double-entry general ledger with AP, AR, bank reconciliation, multi-currency and tax — auditable, automated and connected to every other module.",
    features: [
      { icon: Calculator, title: "General ledger", description: "Double-entry with full audit trail." },
      { icon: Receipt, title: "AP & AR", description: "Bills, invoices, payments and aging." },
      { icon: ShieldCheck, title: "Bank reconciliation", description: "Auto-match feeds from any bank." },
      { icon: BarChart3, title: "Multi-currency", description: "Revaluation, gains/losses and consolidation." },
      { icon: FileText, title: "Tax compliance", description: "VAT, GST, sales tax and e-invoicing." },
      { icon: Workflow, title: "Period close", description: "Soft close, hard close and locking." },
    ],
    process: [
      { step: "01", title: "Chart of accounts", description: "Design or migrate your CoA." },
      { step: "02", title: "Migrate balances", description: "Open balances, AR/AP aging, fixed assets." },
      { step: "03", title: "Connect banks", description: "Feeds, rules and reconciliation." },
      { step: "04", title: "Parallel run", description: "Validate one period against old system." },
    ],
    deliverables: ["Chart of accounts", "Opening balances", "Bank feeds", "Tax setup", "Financial reports"],
    techStack: ["ERPNext", "Odoo Accounting", "Plaid", "Stripe", "QuickBooks API"],
    pricing: [
      { name: "Starter", price: "$4k", features: ["Single entity", "Up to 3 users", "Basic reports"] },
      { name: "Business", price: "$12k", period: "project", highlighted: true, features: ["Multi-currency", "Tax", "Fixed assets"] },
      { name: "Enterprise", price: "Custom", features: ["Multi-entity consolidation", "Audit pack", "IFRS/GAAP"] },
    ],
    faqs: [
      { q: "Can we migrate from QuickBooks?", a: "Yes — CoA, customers, vendors, open invoices and history." },
      { q: "Audit-ready?", a: "Yes — full trails, locking and exportable journal files." },
    ],
  },
  payroll: {
    eyebrow: "ERP · Finance",
    title: "Payroll Module",
    sub: "Compliant, automated payroll integrated with HR and accounting.",
    metaDesc: "ERP Payroll module: salary structures, statutory deductions, payslips, multi-country payroll and direct deposit.",
    intro:
      "Run payroll in minutes with country-specific tax tables, statutory deductions and direct-deposit files — fully posted to your GL.",
    features: [
      { icon: Wallet, title: "Salary structures", description: "Components, formulas and grade bands." },
      { icon: ShieldCheck, title: "Statutory compliance", description: "Tax, social security and pension." },
      { icon: FileText, title: "Payslips", description: "Branded PDF + employee self-service portal." },
      { icon: Workflow, title: "Multi-country", description: "Local payroll engines per country." },
      { icon: BarChart3, title: "Cost analytics", description: "Headcount and cost by department/project." },
      { icon: Bell, title: "Direct deposit", description: "Bank files and payment integrations." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Pay components, statutory and cycles." },
      { step: "02", title: "Configure", description: "Structures, tax tables and approvals." },
      { step: "03", title: "Migrate", description: "Employee master and YTD balances." },
      { step: "04", title: "Parallel run", description: "Match a full cycle against current payroll." },
    ],
    deliverables: ["Salary structures", "Tax setup", "Payslip templates", "Bank file", "Self-service portal"],
    techStack: ["ERPNext HR", "Odoo Payroll", "Country-specific engines", "Plaid"],
    pricing: [
      { name: "Starter", price: "$3k", features: ["1 country", "Up to 25 employees", "Standard payslips"] },
      { name: "Business", price: "$8k", period: "project", highlighted: true, features: ["Multi-country", "Approvals", "Self-service portal"] },
      { name: "Enterprise", price: "Custom", features: ["Complex bonuses", "Equity & stock", "Audit reports"] },
    ],
    faqs: [
      { q: "Which countries do you support?", a: "US, UK, EU, GCC, India, Pakistan and 30+ more via local engines." },
      { q: "Does it post to accounting?", a: "Yes — every payroll auto-generates GL entries." },
    ],
  },
  reporting: {
    eyebrow: "ERP · Finance",
    title: "Reporting Module",
    sub: "Real-time dashboards across every module of your ERP.",
    metaDesc: "ERP Reporting module: P&L, balance sheet, cash flow, KPI dashboards and ad-hoc report builder.",
    intro:
      "Self-service dashboards and scheduled reports across finance, sales, inventory and operations — with drill-down to source transactions.",
    features: [
      { icon: LineChart, title: "Financial reports", description: "P&L, balance sheet, cash flow, trial balance." },
      { icon: BarChart3, title: "KPI dashboards", description: "Role-based dashboards for execs and ops." },
      { icon: Workflow, title: "Report builder", description: "Drag-and-drop ad-hoc reports." },
      { icon: Bell, title: "Scheduled delivery", description: "Auto email/Slack on a cadence." },
      { icon: ShieldCheck, title: "Drill-down", description: "Click any number to see source transactions." },
      { icon: FileText, title: "Exports", description: "PDF, Excel and CSV with branded layouts." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "KPIs that matter to each persona." },
      { step: "02", title: "Build", description: "Dashboards and report templates." },
      { step: "03", title: "Distribute", description: "Roles, schedules and embeds." },
      { step: "04", title: "Iterate", description: "Refine based on usage and feedback." },
    ],
    deliverables: ["Executive dashboard", "Ops dashboards", "Custom reports", "Scheduled emails", "Mobile-friendly views"],
    techStack: ["Metabase", "Apache Superset", "PostgreSQL", "ERPNext reports", "Odoo Studio"],
    pricing: [
      { name: "Starter", price: "$2k", features: ["5 dashboards", "Standard reports", "1 user role"] },
      { name: "Business", price: "$6k", period: "project", highlighted: true, features: ["Custom dashboards", "Scheduled delivery", "Multi-role"] },
      { name: "Enterprise", price: "Custom", features: ["Data warehouse", "Embedded analytics", "Forecasting"] },
    ],
    faqs: [
      { q: "Can we embed dashboards in our portal?", a: "Yes — via signed iframes or SDKs." },
      { q: "Real-time or scheduled?", a: "Both — live queries plus cached snapshots." },
    ],
  },
};

export const Route = createFileRoute("/software/erp/$child")({
  loader: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) throw notFound();
    return { item, slug: params.child };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { item } = loaderData;
    const url = `/software/erp/${params.child}`;
    return {
      meta: [
        { title: `${item.title} — ERP | CoderLity` },
        { name: "description", content: item.metaDesc },
        { property: "og:title", content: `${item.title} — ERP | CoderLity` },
        { property: "og:description", content: item.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: ErpChild,
});

function ErpChild() {
  const { item } = Route.useLoaderData();
  return <ServicePage data={item} />;
}
